/**
 * Parent (family) accounts: sign-up and sign-in on our own pages against the families user pool
 * (separate from the owner's admin pool). Passkey first; password (+ emailed code when the pool
 * has SES email) as the fallback. Tokens live in session storage only, like the admin.
 * In `vite dev` against the local API, requests carry a dev role header instead.
 */
import { InviteCode, type ApiError } from '@ka/contracts';
import {
  AuthError,
  cognitoCall,
  createPasskey,
  getPasskey,
  passkeysSupported,
  type CognitoConfig,
} from '@ka/ui/cognito';

export { AuthError, passkeysSupported };

export const isDev = import.meta.env.DEV && import.meta.env.VITE_REAL_AUTH !== '1';
const TOKEN_KEY = 'ka.family.token';

let config: Promise<CognitoConfig> | undefined;
const authConfig = () =>
  (config ??= fetch('/config.json', { cache: 'no-store' }).then((r) => {
    if (!r.ok) throw new AuthError('NoConfig', 'Family sign-in is not available yet.');
    return r.json() as Promise<CognitoConfig>;
  }));

const FRIENDLY: Record<string, string> = {
  NotAuthorizedException: 'That email, password or code was not accepted.',
  UserNotFoundException: 'That email, password or code was not accepted.',
  CodeMismatchException: 'That code was not accepted. Check the newest email from us.',
  ExpiredCodeException: 'That code has expired. Ask for a new one.',
  UsernameExistsException: 'There is already an account with this email. Try signing in.',
  InvalidPasswordException: 'Please use a longer password (at least 12 characters, with a number).',
  LimitExceededException: 'Too many tries. Please wait a few minutes and try again.',
  TooManyRequestsException: 'Too many tries. Please wait a few minutes and try again.',
  // The pre-sign-up trigger rejected the invite code.
  UserLambdaValidationException:
    'That invite code did not work. Each card’s code works for one family; check the code or ask us for a new card.',
};

// ---------- invite from the card's QR (kept for this tab only) ----------

const INVITE_KEY = 'ka.family.invite';
export function rememberInvite(raw: string | null): void {
  const r = InviteCode.safeParse(raw ?? '');
  if (r.success) sessionStorage.setItem(INVITE_KEY, r.data);
}
export const storedInvite = () => sessionStorage.getItem(INVITE_KEY) ?? '';
export const forgetInvite = () => sessionStorage.removeItem(INVITE_KEY);

async function cognito<T>(target: string, body: Record<string, unknown>): Promise<T> {
  return cognitoCall<T>(await authConfig(), target, body, FRIENDLY);
}

interface AuthResponse {
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
  AuthenticationResult?: { AccessToken: string; ExpiresIn: number };
}

/** What the sign-in screen must ask for next. */
export type Step = { kind: 'done' } | { kind: 'emailCode'; username: string; session: string };

function next(username: string, r: AuthResponse): Step {
  if (r.AuthenticationResult) {
    sessionStorage.setItem(
      TOKEN_KEY,
      JSON.stringify({
        access: r.AuthenticationResult.AccessToken,
        exp: Date.now() + r.AuthenticationResult.ExpiresIn * 1000,
      }),
    );
    return { kind: 'done' };
  }
  if (r.ChallengeName === 'EMAIL_OTP')
    return { kind: 'emailCode', username, session: r.Session ?? '' };
  throw new AuthError('Unsupported', `Unexpected sign-in step (${r.ChallengeName ?? 'none'}).`);
}

/** The families pool's pre-sign-up trigger checks `inviteCode` (single use, one per card). */
export async function signUp(email: string, password: string, invite: string): Promise<void> {
  const c = await authConfig();
  await cognito('SignUp', {
    ClientId: c.clientId,
    Username: email,
    Password: password,
    UserAttributes: [{ Name: 'email', Value: email }],
    ValidationData: [{ Name: 'inviteCode', Value: invite.trim().toUpperCase() }],
  });
  forgetInvite();
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  const c = await authConfig();
  await cognito('ConfirmSignUp', {
    ClientId: c.clientId,
    Username: email,
    ConfirmationCode: code.trim(),
  });
}

export async function resendSignUpCode(email: string): Promise<void> {
  const c = await authConfig();
  await cognito('ResendConfirmationCode', { ClientId: c.clientId, Username: email });
}

export async function signInWithPassword(email: string, password: string): Promise<Step> {
  const c = await authConfig();
  const r = await cognito<AuthResponse>('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: c.clientId,
    AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: 'PASSWORD', PASSWORD: password },
  });
  return next(email, r);
}

/** Throws AuthError('NoPasskey') when the account has none yet. */
export async function signInWithPasskey(email: string): Promise<Step> {
  const c = await authConfig();
  const r = await cognito<AuthResponse>('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: c.clientId,
    AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: 'WEB_AUTHN' },
  });
  if (r.ChallengeName !== 'WEB_AUTHN')
    throw new AuthError(
      'NoPasskey',
      'No passkey on this account yet. Sign in with your password, then add one.',
    );
  const credential = await getPasskey(r.ChallengeParameters!.CREDENTIAL_REQUEST_OPTIONS!);
  const done = await cognito<AuthResponse>('RespondToAuthChallenge', {
    ClientId: c.clientId,
    ChallengeName: 'WEB_AUTHN',
    Session: r.Session,
    ChallengeResponses: { USERNAME: email, CREDENTIAL: credential },
  });
  return next(email, done);
}

export async function submitEmailCode(
  step: { username: string; session: string },
  code: string,
): Promise<Step> {
  const c = await authConfig();
  const r = await cognito<AuthResponse>('RespondToAuthChallenge', {
    ClientId: c.clientId,
    ChallengeName: 'EMAIL_OTP',
    Session: step.session,
    ChallengeResponses: { USERNAME: step.username, EMAIL_OTP_CODE: code.trim() },
  });
  return next(step.username, r);
}

export async function forgotPassword(email: string): Promise<void> {
  const c = await authConfig();
  await cognito('ForgotPassword', { ClientId: c.clientId, Username: email });
}

export async function resetPassword(email: string, code: string, password: string) {
  const c = await authConfig();
  await cognito('ConfirmForgotPassword', {
    ClientId: c.clientId,
    Username: email,
    ConfirmationCode: code.trim(),
    Password: password,
  });
}

// ---------- signed-in session ----------

export function accessToken(): string | null {
  try {
    const t = JSON.parse(sessionStorage.getItem(TOKEN_KEY) ?? 'null') as {
      access: string;
      exp: number;
    } | null;
    return t && t.exp > Date.now() + 30_000 ? t.access : null;
  } catch {
    return null;
  }
}

export const isSignedIn = () => isDev || !!accessToken();

function requireToken() {
  const t = accessToken();
  if (!t) throw new AuthError('NotSignedIn', 'Please sign in again.');
  return t;
}

export async function signOut(): Promise<void> {
  const token = accessToken();
  sessionStorage.removeItem(TOKEN_KEY);
  if (token) await cognito('GlobalSignOut', { AccessToken: token }).catch(() => undefined);
}

export async function addPasskey(): Promise<void> {
  const AccessToken = requireToken();
  const { CredentialCreationOptions: o } = await cognito<{
    CredentialCreationOptions: Parameters<typeof createPasskey>[0];
  }>('StartWebAuthnRegistration', { AccessToken });
  await cognito('CompleteWebAuthnRegistration', {
    AccessToken,
    Credential: await createPasskey(o),
  });
}

export class FamilyApiError extends Error {
  constructor(
    readonly status: number,
    body: ApiError | null,
  ) {
    super(body?.error.message ?? `Request failed (${status})`);
  }
}

export async function familyApi<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (isDev) headers['x-ka-dev-role'] = 'family';
  else headers.authorization = `Bearer ${requireToken()}`;
  const res = await fetch(`/api/family${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) throw new FamilyApiError(res.status, json as ApiError | null);
  return json as T;
}

/** Deletes every kid profile, then the parent's Cognito user. */
export async function deleteFamilyAccount(): Promise<void> {
  await familyApi('DELETE', '');
  if (!isDev) await cognito('DeleteUser', { AccessToken: requireToken() });
  sessionStorage.removeItem(TOKEN_KEY);
}
